import React, { useState, useRef } from 'react';
import {
    Box,
    IconButton,
    Typography,
    Avatar,
    Divider,
    Menu,
    MenuItem,
    ListItemIcon,
} from '@mui/material';
import { ArrowBackIcon, MoreHorizIcon, EditOutlinedIcon, SaveAltOutlinedIcon, EmailOutlinedIcon, DeleteOutlineIcon, OpenInNewIcon } from 'icons/materialIcons';
import avatarPlaceholder from 'images/avatar-cropped.png';
import DarkButtonWithIcon from 'components/common/button/DarkButtonWithIcon';
import WhiteButtonWithIcon from 'components/common/button/WhiteButtonWithIcon';
import { downloadVCard } from 'utils/vcfUtils';
import RemoveLeadModal from './RemoveLeadModal';
import UpgradeSnackBar from 'components/common/notification/UpgradeSnackBar';
import { deleteLead } from 'apis/leadCaptureApi';
import { useNavigate } from 'react-router-dom';
import { useLeadCaptureContext } from 'contexts/LeadCaptureContext';
import SendLeadEmailModal from 'components/contacts/contactDetails/SendLeadEmailModal';
import ExportModal from 'components/contacts/contactMain/contactsMenu/ExportModal';

const ContactDetailMenu = ({ contact, onBack, onEditClick, onDeleteComplete }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showUpgradeSnackbar, setShowUpgradeSnackbar] = useState(false);
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const exportButtonRef = useRef(null);
    const open = Boolean(anchorEl);
    const navigate = useNavigate();
    const { deleteContact } = useLeadCaptureContext();

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleSubscribe = () => {
        setShowUpgradeSnackbar(false);
        navigate('/dashboard/subscribe');
    };

    // Function to handle email click
    const handleEmailClick = () => {
        handleMenuClose();
        setIsEmailModalOpen(true);
    };

    // Export button click handler
    const handleExportClick = (event) => {
        exportButtonRef.current = event.currentTarget;
        setIsExportModalOpen(true);
    };

    // Export modal close handler
    const handleExportModalClose = () => {
        setIsExportModalOpen(false);
    };

    // Prepare contact data in the format expected by downloadVCard
    const prepareContactData = () => {
        return {
            contact_info: {
                fullName: contact.leadName,
                email: contact.leadEmail,
                phoneNumber: contact.leadPhoneNumber,
                company: contact.company || contact.leadCompany,
                jobTitle: contact.jobTitle || contact.leadJobTitle,
                location: contact.leadLocation
            },
            bio: contact.leadNotes,
            profilePictureUrl: contact.profilePictureUrl
        };
    };

    // Format display name based on available contact info
    const displayName = contact ?
        (contact.leadName ?
            `${contact.leadName}${contact.leadJobTitle ? ` - ${contact.leadJobTitle}` : ''}${contact.leadCompany ? ` @ ${contact.leadCompany}` : ''}`
            : contact.leadEmail || 'Unknown')
        : 'Unknown';

    // Handle opening remove modal
    const handleRemoveClick = () => {
        handleMenuClose();
        setShowRemoveModal(true);
    };

    // Handle cancel remove
    const handleCancelRemove = () => {
        setShowRemoveModal(false);
    };

    // Handle confirm remove
    const handleConfirmRemove = async () => {
        try {
            setIsDeleting(true);
            await deleteLead(contact._id);
            deleteContact(contact._id);
            setShowRemoveModal(false);
            if (onDeleteComplete) {
                onDeleteComplete();
            }
        } catch (error) {
            // Error handling for lead deletion
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 3,
                    width: '100%'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                        onClick={onBack}
                        sx={{
                            color: '#4f4f4f',
                            '&:hover': { backgroundColor: '#f5f5f5' }
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>

                    <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                            height: 32,
                            my: 'auto'
                        }}
                    />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                            src={contact?.profilePictureUrl || avatarPlaceholder}
                            sx={{ width: 44, height: 44 }}
                            imgProps={{
                                loading: "eager",
                                referrerPolicy: "no-referrer",
                                crossOrigin: "anonymous"
                            }}
                        />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#242020' }}>
                            {displayName}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <DarkButtonWithIcon
                        text="Export"
                        icon={<OpenInNewIcon
                            sx={{
                                fontSize: 20,
                                color: '#fff',
                                transform: 'scaleX(-1)'
                            }}
                        />}
                        onClick={handleExportClick}
                        sx={{ fontWeight: '700' }}
                        ref={exportButtonRef}
                    />

                    <WhiteButtonWithIcon
                        icon={MoreHorizIcon}
                        text=""
                        onClick={handleMenuClick}
                        sx={{
                            width: '80px',
                            minWidth: '80px',
                            height: '40px',
                            padding: '0 16px',
                            borderRadius: '100px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            border: '1px solid #E0E0E0',
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                                border: '1px solid #E0E0E0'
                            }
                        }}
                        iconProps={{
                            fontSize: '20px',
                            sx: { color: '#4f4f4f' }
                        }}
                    />

                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleMenuClose}
                        PaperProps={{
                            sx: {
                                mt: 1,
                                borderRadius: '12px',
                                minWidth: 200,
                                boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
                            },
                        }}
                    >
                        {[
                            {
                                icon: EditOutlinedIcon,
                                text: 'Edit',
                                onClick: () => {
                                    handleMenuClose();
                                    if (onEditClick) onEditClick();
                                },
                                dataAttr: "data-edit-contact-button" // Add data attribute for edit button
                            },
                            {
                                icon: SaveAltOutlinedIcon,
                                text: 'Save as contact',
                                onClick: () => {
                                    const contactData = prepareContactData();
                                    downloadVCard(contactData);
                                    handleMenuClose();
                                }
                            },
                            {
                                icon: EmailOutlinedIcon,
                                text: 'Email lead',
                                onClick: handleEmailClick
                            },
                            {
                                icon: DeleteOutlineIcon,
                                text: 'Remove',
                                sx: { color: 'error.main' },
                                onClick: handleRemoveClick
                            },
                        ].map((item, index) => (
                            <MenuItem
                                key={index}
                                onClick={() => {
                                    if (item.onClick) {
                                        item.onClick();
                                    } else {
                                        handleMenuClose();
                                    }
                                }}
                                sx={{
                                    py: 1.5,
                                    px: 2,
                                    '&:hover': {
                                        backgroundColor: '#f5f5f5',
                                    },
                                    ...item.sx,
                                }}
                                {...(item.dataAttr ? { [item.dataAttr]: true } : {})} // Add data attribute if specified
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 36,
                                        ...(item.sx || {})
                                    }}
                                >
                                    <item.icon sx={{ fontSize: 20, ...(item.sx || {}) }} />
                                </ListItemIcon>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        ...item.sx
                                    }}
                                >
                                    {item.text}
                                </Typography>
                            </MenuItem>
                        ))}
                    </Menu>

                    <ExportModal
                        isOpen={isExportModalOpen}
                        onClose={handleExportModalClose}
                        anchorEl={exportButtonRef.current}
                        contacts={contact}
                        isSingleContact={true}
                    />
                </Box>
            </Box>

            <RemoveLeadModal
                open={showRemoveModal}
                onClose={handleCancelRemove}
                onConfirm={handleConfirmRemove}
                isDeleting={isDeleting}
            />

            <SendLeadEmailModal
                open={isEmailModalOpen}
                onClose={() => setIsEmailModalOpen(false)}
                contact={contact}
            />

            <UpgradeSnackBar
                open={showUpgradeSnackbar}
                handleClose={() => setShowUpgradeSnackbar(false)}
                handleSubscribe={handleSubscribe}
                message="Upgrade to Pro or Pro Plus for export access"
            />
        </>
    );
};

export default ContactDetailMenu;